// Whether the signed-in user may add, change or delete the costs, keys and
// settlements of a property. Same rule as the API's create-access guard (and
// as payment orders): anyone working for an agency, or a user with create
// access. An owner who logs in on a property the agency manages only reads.
// With a property, a user with create access who does not work for an agency
// (a landlord) also needs the whole of that property: on a group with other
// owners they only read their own part, and the API refuses their changes
// (GET /settlements/edit-access answers that).

export interface ICostsEditPermission {
  canEdit: boolean;
  // The API's answer for the property is still on its way: `canEdit` is then
  // false only for now, so a page must not act on it yet (e.g. send the user
  // away).
  isLoading: boolean;
}

// The user is asked about with the property only when the role alone does not
// decide: a landlord with create access, on a property.
export const needsCostsEditCheck = (params: {
  isBroker: boolean;
  hasCreateAccess: boolean;
  propertyId?: string | number;
}): boolean => {
  const { isBroker, hasCreateAccess, propertyId } = params;

  return (
    !isBroker && hasCreateAccess && propertyId != null && propertyId !== ""
  );
};

export const resolveCostsEditPermission = (params: {
  isBroker: boolean;
  hasCreateAccess: boolean;
  propertyId?: string | number;
  // GET /settlements/edit-access for the property: null while it loads.
  propertyAccess: boolean | null;
}): ICostsEditPermission => {
  const { isBroker, hasCreateAccess, propertyAccess } = params;

  if (!needsCostsEditCheck(params)) {
    return { canEdit: isBroker || hasCreateAccess, isLoading: false };
  }

  return {
    canEdit: propertyAccess === true,
    isLoading: propertyAccess === null,
  };
};

// A saved settlement shown in the drawer is only read: its property is
// archived (nothing of it changes until it is activated again, like its
// costs), or it is opened on another property's page: a group settlement on
// one of the group's units, or a unit's settlement or departure on its
// building (Q6 of 2026-10-16). Each is managed on its own property. Read
// only = no delete and no tenant actions.
export const isSettlementViewReadOnly = (params: {
  isPropertyArchived: boolean;
  scope?: "group" | "unit";
  groupId?: string;
  unitId?: string;
  pagePropertyId?: string;
}): boolean => {
  const { isPropertyArchived, scope, groupId, unitId, pagePropertyId } = params;
  if (isPropertyArchived) return true;
  if (!pagePropertyId) return false;

  return scope === "group"
    ? pagePropertyId !== groupId
    : !!unitId && pagePropertyId !== unitId;
};

// The settlement wizard sends the user back to the costs when it would make
// or finish a settlement they may not: they may only read the costs, or the
// property is archived. A confirmed settlement stays readable.
export const mustLeaveSettlementWizard = (params: {
  isPropertyArchived: boolean;
  canEdit: boolean;
  isCheckingEditAccess: boolean;
  isNew: boolean;
  isConfirmed: boolean;
}): boolean => {
  const {
    isPropertyArchived,
    canEdit,
    isCheckingEditAccess,
    isNew,
    isConfirmed,
  } = params;
  const mayNotChange =
    isPropertyArchived || (!canEdit && !isCheckingEditAccess);

  return mayNotChange && (isNew || !isConfirmed);
};
